// ── Bolsa de horas por trabajador (web) ────────────────────────────────────
// Punto ÚNICO donde la web prepara los datos de la bolsa de horas de un
// trabajador. Lo usan:
//   · WorkerDetailPage  → ficha del trabajador (HourBalanceCard).
//   · InformesPage      → "Mi actividad" (HourBalanceCard del propio usuario).
//   · EquipoPage        → chip de bolsa de cada fila (total auto + manual).
//   · CompararCalendariosPage / AbsenceApprovalModal → mismo total.
// Replica EXACTAMENTE el cómputo de la app (WorkerDetailScreen): eventos del
// rango balanceEventsRange (1-ene del año pasado → 31-dic del actual),
// histórico de horarios enriquecido con los slots reales de cada versión +
// versión virtual con el horario actual (src/lib/scheduleHistory.js,
// compartido con la app), festivos del trabajador de TODOS los años del
// cómputo (balanceHolidayYears) y ajustes manuales. Total = auto + manual.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getEventsByRange, getWorkHours, getWorkHoursAtDate,
  getScheduleVersionHistory, getHourBalance,
} from '../services/api';
import {
  computeBalanceTotals, eligibleForBalance,
  balanceEventsRange, balanceHolidayYears,
} from '../lib/hourBalance';
import { fetchHolidaysSetForWorker } from '../lib/spainHolidays';
import {
  enrichScheduleVersions, buildEnrichedScheduleHistory, balanceScheduleInput, scheduleByDateInput,
} from '../lib/scheduleHistory';
import { timeZoneForWorker, addDaysToDayKey } from '../lib/workerTime';

// Re-exportados por compatibilidad: varias páginas los importan desde aquí.
// La lógica vive en los archivos COMPARTIDOS con la app (src/lib).
// computeBalanceTotals (total auto + manual, con el flag `noSchedule` cuando
// el trabajador no tiene horario) vive ahora en src/lib/hourBalance.js,
// compartido con la app (WorkerDetailScreen).
export { balanceEventsRange, buildEnrichedScheduleHistory, balanceScheduleInput, computeBalanceTotals };

// Set<ISO> de festivos de un trabajador para los años indicados. Misma regla
// que useHolidays (comunidad + PROVINCIA + LOCALIDAD + EMPRESA; sin comunidad
// ni provincia → sin oficiales, pero sí los de su empresa), para que el total
// del chip coincida con la ficha.
const fetchWorkerHolidaysSet = async (worker, years) => {
  const hw = {
    autonomousCommunity: worker?.autonomousCommunity || worker?.community || null,
    province: worker?.province || null,
    locality: worker?.locality || null,
    companyId: worker?.companyId ?? null,
  };
  if (!Array.isArray(years) || years.length === 0 || (!hw.autonomousCommunity && !hw.province && !hw.companyId)) return new Set();
  const { set } = await fetchHolidaysSetForWorker(hw, years);
  return set;
};

// Histórico de versiones de horario con los slots REALES de cada versión
// (la parte pura, enrichScheduleVersions, es compartida con la app). Rechaza
// si falla /history; las peticiones por versión son best-effort.
export const fetchEnrichedScheduleHistory = async (workerId) => {
  const versions = await getScheduleVersionHistory(workerId);
  return enrichScheduleVersions(versions, (iso) => getWorkHoursAtDate(workerId, iso));
};

// ── Carga completa + caché de sesión + límite de concurrencia ─────────────
// Cada trabajador necesita: eventos (2 años), workhours, histórico de
// horarios (+1 petición por versión sin slots), ajustes manuales y festivos.
// Para listas grandes (Equipo) limitamos a MAX_CONCURRENT trabajadores a la
// vez y cacheamos el resultado durante la sesión (con TTL, y se invalida al
// tocar la bolsa desde la ficha).
const MAX_CONCURRENT = 3;
const CACHE_TTL_MS = 5 * 60 * 1000;
const balanceCache = new Map(); // workerId → { at, promise }
let running = 0;
const queue = []; // [{ id, exec, reject }]

const pump = () => {
  while (running < MAX_CONCURRENT && queue.length) {
    const item = queue.shift();
    item.exec();
  }
};

const runLimited = (id, task) => new Promise((resolve, reject) => {
  const exec = () => {
    running += 1;
    task().then(resolve, reject).finally(() => {
      running -= 1;
      pump();
    });
  };
  queue.push({ id, exec, reject });
  pump();
});

const CANCELLED = 'hour-balance-cancelled';

// Descarta las cargas que siguen EN COLA (no las que ya están en marcha) de
// los ids indicados. Se usa al desmontar una página para no seguir pidiendo
// la bolsa de 200 trabajadores que ya nadie va a ver.
export const cancelPendingHourBalanceLoads = (ids) => {
  const set = ids ? new Set(ids) : null;
  for (let i = queue.length - 1; i >= 0; i -= 1) {
    const item = queue[i];
    if (set && !set.has(item.id)) continue;
    queue.splice(i, 1);
    balanceCache.delete(item.id);
    const err = new Error('Carga de bolsa cancelada');
    err.code = CANCELLED;
    item.reject(err);
  }
};

export const isCancelledHourBalanceLoad = (err) => err?.code === CANCELLED;

// Invalida la caché de un trabajador (o de todos si no se pasa id). Llamarlo
// cuando cambie algo que afecte a la bolsa (ajustes, eventos, horario).
export const invalidateWorkerHourBalance = (workerId) => {
  if (workerId == null) balanceCache.clear();
  else balanceCache.delete(Number(workerId));
};

const settledArray = (r) => (r.status === 'fulfilled' && Array.isArray(r.value) ? r.value : []);

// Devuelve Promise<{ noSchedule, auto, manual, total, byDay, manualCount }>
// (sin horario asignado → noSchedule: true, auto/total: null). Rechaza si
// fallan los eventos o los ajustes manuales (sin ellos el total no es fiable);
// workhours / histórico / festivos son best-effort, igual que en la ficha.
export const loadWorkerHourBalance = (worker, { force = false } = {}) => {
  const id = worker?.id != null ? Number(worker.id) : null;
  if (id == null || !Number.isFinite(id)) return Promise.reject(new Error('Trabajador sin id'));
  const hit = balanceCache.get(id);
  if (!force && hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;

  // Zona del trabajador (Canarias / península): decide a qué día pertenece
  // cada fichaje en el cómputo, igual en cualquier navegador.
  const tz = timeZoneForWorker(worker);
  const promise = runLimited(id, async () => {
    const { start, end } = balanceEventsRange(new Date(), tz);
    const [evRes, whRes, offRes, histRes, holRes] = await Promise.allSettled([
      // start - 1: el día de Madrid empieza a las 22:00/23:00 UTC del anterior;
      // sin esto se perderían los fichajes de madrugada del primer día.
      getEventsByRange(id, addDaysToDayKey(start, -1), end),
      getWorkHours(id),
      getHourBalance(id),
      fetchEnrichedScheduleHistory(id),
      fetchWorkerHolidaysSet(worker, balanceHolidayYears(new Date(), tz)),
    ]);
    if (evRes.status === 'rejected') throw evRes.reason;
    if (offRes.status === 'rejected') throw offRes.reason;
    const workhours = settledArray(whRes);
    const enriched = buildEnrichedScheduleHistory(settledArray(histRes), workhours);
    const holidaysSet = holRes.status === 'fulfilled' && holRes.value ? holRes.value : new Set();
    return computeBalanceTotals(
      settledArray(evRes),
      balanceScheduleInput(enriched, workhours),
      holidaysSet,
      settledArray(offRes),
      tz,
    );
  });
  balanceCache.set(id, { at: Date.now(), promise });
  promise.catch(() => {
    if (balanceCache.get(id)?.promise === promise) balanceCache.delete(id);
  });
  return promise;
};

// ── Hooks ─────────────────────────────────────────────────────────────────

// Histórico de horarios (enriquecido) + workhours actuales de un trabajador,
// cargados al montar. Devuelve el `scheduleInput` listo para HourBalanceCard
// y `scheduleByDate` (histórico o, sin él, Map<dow, slot> actual) para los
// contadores de vacaciones y el descanso del calendario.
export const useWorkerSchedule = (workerId) => {
  const [state, setState] = useState({ workhours: [], scheduleHistory: [], loading: !!workerId });

  // Contador de peticiones: si cambia el trabajador (o se recarga) antes de
  // que termine una carga anterior, la respuesta vieja se descarta.
  const reqId = useRef(0);
  const load = useCallback(async () => {
    const my = ++reqId.current;
    if (!workerId) { setState({ workhours: [], scheduleHistory: [], loading: false }); return; }
    setState((s) => ({ ...s, loading: true }));
    const [whRes, histRes] = await Promise.allSettled([
      getWorkHours(workerId),
      fetchEnrichedScheduleHistory(workerId),
    ]);
    if (my !== reqId.current) return;
    setState({
      workhours: settledArray(whRes),
      scheduleHistory: settledArray(histRes),
      loading: false,
    });
  }, [workerId]);

  // Se carga en cada montaje: nada de caché entre navegaciones.
  useEffect(() => {
    load();
    return () => { reqId.current += 1; };
  }, [load]);

  // Memo: los consumidores (calendario, contadores) dependen de la identidad.
  return useMemo(() => {
    const enriched = buildEnrichedScheduleHistory(state.scheduleHistory, state.workhours);
    return {
      workhours: state.workhours,
      scheduleHistory: state.scheduleHistory,
      enrichedScheduleHistory: enriched,
      scheduleInput: balanceScheduleInput(enriched, state.workhours),
      scheduleByDate: scheduleByDateInput(enriched, state.workhours),
      loading: state.loading,
      reload: load,
    };
  }, [state, load]);
};

// Carga perezosa de la bolsa (total auto + manual) de VARIOS trabajadores.
// `request(worker)` encola la carga (si es elegible y no se pidió ya);
// `balances` es un Map<workerId, { status: 'loading'|'done'|'error', total, auto, manual, noSchedule }>.
// Con `noSchedule: true` el total es null: pintar "Sin horario", nunca un número.
// Al desmontar, cancela lo que siga en cola.
export const useLazyHourBalances = () => {
  const [balances, setBalances] = useState(() => new Map());
  const requested = useRef(new Set());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const ids = requested.current;
    return () => {
      mounted.current = false;
      cancelPendingHourBalanceLoads(Array.from(ids));
      ids.clear();
    };
  }, []);

  const setEntry = useCallback((id, entry) => {
    setBalances((prev) => {
      const next = new Map(prev);
      if (entry == null) next.delete(id); else next.set(id, entry);
      return next;
    });
  }, []);

  const request = useCallback((worker, { force = false } = {}) => {
    if (!worker || worker.id == null || !eligibleForBalance(worker)) return;
    const id = Number(worker.id);
    if (!force && requested.current.has(id)) return;
    requested.current.add(id);
    if (force) invalidateWorkerHourBalance(id);
    setEntry(id, { status: 'loading' });
    loadWorkerHourBalance(worker, { force })
      .then((r) => {
        if (!mounted.current) return;
        setEntry(id, { status: 'done', total: r.total, auto: r.auto, manual: r.manual, noSchedule: !!r.noSchedule });
      })
      .catch((err) => {
        if (!mounted.current) return;
        requested.current.delete(id);
        // Cancelada (p.ej. StrictMode remonta): la dejamos sin estado para
        // que se vuelva a pedir cuando haga falta.
        setEntry(id, isCancelledHourBalanceLoad(err) ? null : { status: 'error' });
      });
  }, [setEntry]);

  return { balances, request };
};
